/**
 * 日期格式化工具
 * 格式字符串定义
 *
 * <pre>
 * yyyy : 4位数年份
 * yy : 2位数年份
 * MM : 月份全拼（英文），例如:January、March
 * M :月份缩写（英文），3个字母
 * mm : 月份，2位，小于10时前面补零
 * m : 月份，小于10时前面不补零
 * dd : 一个月中的第几天，小于10时前面补零
 * d : 一个月中的第几天，小于10时前面不补零
 * HH : 小时，2位，小于10时前面补零---12小时制
 * H : 小时，小于10时前面不补零---12小时制
 * hh : 小时，2位，小于10时前面补零---24小时制
 * h : 小时，小于10时前面不补零---24小时制
 * ii : 分钟，2位，小于10时前面补零
 * i : 分钟，小于10时前面不补零
 * ss :秒，2位，小于10时前面补零
 * s : 秒，小于10前面不补0
 * p : 上午还是下午，小写字母 ('am' 或 'pm')
 * </pre>
 */
import { ParseFormatConfig } from './types';

var MILLIS_PER_SECOND = 1000,
  MILLIS_PER_MINUTE = 60 * MILLIS_PER_SECOND,
  MILLIS_PER_HOUR = 60 * MILLIS_PER_MINUTE,
  MILLIS_PER_DAY = 24 * MILLIS_PER_HOUR,
  NON_PUNCTUATION_EXPRESSION = /[^ -\/:-@\[-`{-~\t\n\rTZ]+/g; // eslint-disable-line

/**
 * 获取一天的起始时间点
 * @param {Date} date 日期
 * @returns {Date} 日期起始点
 */
function getStartOfDay(date:Date) {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0, 0);
}

/**
 * 获取一天的结束时间点
 * @param {Date} date 日期
 * @returns {Date} 日期结束点
 */
function getEndOfDay(date:Date) {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate(), 23, 59, 59, 999);
}

/**
 * 获取每月的的第一天
 * @param {Date} date 日期
 * @returns {Date} 每月的的第一天
 */
function getStartOfMonth(ds:Date) {
  return new Date(ds.getFullYear(), ds.getMonth(), 1);
}

/**
 * 获取每月的的最后一天
 * @param {Date} date 日期
 * @returns {Date} 每月的的最后一天
 */
function getEndOfMonth(df:Date) {
  var currentMonth = df.getMonth();
  var nextMonth = ++currentMonth;
  var nextMonthFirstDay = new Date(df.getFullYear(), nextMonth, 1);
  var oneDay = MILLIS_PER_DAY;
  return new Date(nextMonthFirstDay.valueOf() - oneDay);
}
/**
 * 在指定的时间点上加上指定的年数
 * @param {Date} date 日期
 * @param {Number} years 年数
 * @returns {Date} 日期
 */
function addYears(date:Date, years:number) {
  var result = new Date(date.getTime());
  result.setFullYear(date.getFullYear() + years);
  return result;
}

/**
 * 在指定的时间点上加上指定的月数
 * @param {Date} date 日期
 * @param {Number} months 月数
 * @returns {Date} 日期
 */
function addMonths(date:Date, months:number) {
  var result = new Date(date.getTime());
  result.setMonth(date.getMonth() + months);
  result = handleShorterMonth(date, result);
  return result;
}

/**
 * 在指定的时间点上加上指定的天数
 * @param {Date} date 日期
 * @param {Number} days 天数
 * @returns {Date} 日期
 */
function addDays(date:Date, days:number) {
  return add(date, MILLIS_PER_DAY, days);
}

/**
 * 在指定的时间点上加上指定的小时数
 * @param {Date} date 日期
 * @param {Number} hours 小时数
 * @returns {Date} 日期
 */
function addHours(date:Date, hours:number) {
  return add(date, MILLIS_PER_HOUR, hours);
}

/**
 * 在指定的时间点上加上指定的分钟数
 * @param {Date} date 日期
 * @param {Number} minutes 分钟数
 * @returns {Date} 日期
 */
function addMinutes(date:Date, minutes:number) {
  return add(date, MILLIS_PER_MINUTE, minutes);
}

function add(date:Date, multiplier:number, num:number) {
  var resultTime = date.getTime() + multiplier * num;
  return new Date(resultTime);
}

function handleShorterMonth(originalDate:Date, newDate:Date) {
  var result = newDate;
  var originalDayOfMonth = originalDate.getDate();
  if (originalDayOfMonth > result.getDate()) {
    result = addDays(newDate, -newDate.getDate());
  }
  return result;
}

/**
 * 日期格式化
 * @method format
 * @param {Date} date 日期
 * @param format 格式化字符串
 * @returns {String} 返回格式化后的字符串
 */
function formatDate(date:Date, format:string) {
  var meridian = ['am', 'pm'],
    monthsShort = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
    months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'],
    daysShort = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'],
    days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'],
    val: any = {
      // year
      yy: date
        .getFullYear()
        .toString()
        .substring(2),
      yyyy: date.getFullYear(),
      // month
      m: date.getMonth() + 1,
      M: monthsShort[date.getMonth()],
      MM: months[date.getMonth()],
      // day
      d: date.getDate(),
      D: daysShort[date.getDay()],
      DD: days[date.getDay()],
      p: meridian.length == 2 ? meridian[date.getHours() < 12 ? 0 : 1] : '',
      // hour
      h: date.getHours(),
      // minute
      i: date.getMinutes(),
      // second
      s: date.getSeconds(),
    };

  const formatted = parseFormat(format);

  if (meridian.length == 2) {
    val.H = val.h % 12 == 0 ? 12 : val.h % 12;
  } else {
    val.H = val.h;
  }
  val.HH = (val.H < 10 ? '0' : '') + val.H;
  val.P = val.p.toUpperCase();
  val.hh = (val.h < 10 ? '0' : '') + val.h;
  val.ii = (val.i < 10 ? '0' : '') + val.i;
  val.ss = (val.s < 10 ? '0' : '') + val.s;
  val.dd = (val.d < 10 ? '0' : '') + val.d;
  val.mm = (val.m < 10 ? '0' : '') + val.m;

  var newDate = [];
  var seps = formatted.separators.concat();
  for (var i = 0, cnt = formatted.parts.length; i < cnt; i++) {
    if (seps.length) {
      newDate.push(seps.shift());
    }
    newDate.push(val[formatted.parts[i]]);
  }
  if (seps.length) {
    newDate.push(seps.shift());
  }
  return newDate.join('');
}

function parseFormat(format: string): ParseFormatConfig {
  var pattern = /hh?|HH?|p|P|ii?|ss?|dd?|DD?|mm?|MM?|yy(?:yy)?/g,
    separators = format.replace(pattern, '\0').split('\0'),
    parts = format.match(pattern);
  if (!separators || !separators.length || !parts || parts.length == 0) {
    throw new Error('Invalid date format.');
  }
  return {
    separators: separators,
    parts: parts,
  };
}

/**
 * 日期解析
 * @method parse
 * @param {String} date 日期字符串
 * @param {String} format 日期格式
 * @returns {Date} 返回日期对象
 */
function parseDate(date:string|Date, format:string) {
  if (date instanceof Date) {
    return date;
  }

  if (/^[-+]\d+[dmwy]([\s,]+[-+]\d+[dmwy])*$/.test(date)) {
    var part_re = /([-+]\d+)([dmwy])/,
      parts = date.match(/([-+]\d+)([dmwy])/g) || [],
      part,
      dir;
    date = new Date();
    for (var i = 0; i < parts.length; i++) {
      part = part_re.exec(parts[i]) || [];
      dir = parseInt(part[1]);
      switch (part[2]) {
        case 'd':
          date.setDate(date.getDate() + dir);
          break;
        case 'm':
          date = addMonths(date, dir);
          break;
        case 'w':
          date.setDate(date.getDate() + dir * 7);
          break;
        case 'y':
          date = addYears(date, dir);
          break;
      }
    }
    return new Date(date.getFullYear(), date.getMonth(), date.getDate(), date.getHours(), date.getMinutes(), date.getSeconds(), 0);
  }

  var months:any[] = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'],
    monthsShort = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
    meridian = ['am', 'pm'],
    parsed:any = {},
    setters_order = ['hh', 'h', 'ii', 'i', 'ss', 's', 'yyyy', 'yy', 'M', 'MM', 'm', 'mm', 'D', 'DD', 'd', 'dd', 'H', 'HH', 'p', 'P'],
    setters_map:any = {
      hh: function(d:any, v:any) {
        return d.setHours(v);
      },
      h: function(d:any, v:any) {
        return d.setHours(v);
      },
      HH: function(d:any, v:any) {
        return d.setHours(v == 12 ? 0 : v);
      },
      H: function(d:any, v:any) {
        return d.setHours(v == 12 ? 0 : v);
      },
      ii: function(d:any, v:any) {
        return d.setMinutes(v);
      },
      i: function(d:any, v:any) {
        return d.setMinutes(v);
      },
      ss: function(d:any, v:any) {
        return d.setSeconds(v);
      },
      s: function(d:any, v:any) {
        return d.setSeconds(v);
      },
      yyyy: function(d:any, v:any) {
        return d.setFullYear(v);
      },
      yy: function(d:any, v:any) {
        return d.setFullYear(2000 + v);
      },
      m: function(d:any, v:any) {
        v -= 1;
        while (v < 0) v += 12;
        v %= 12;
        d.setMonth(v);
        while (d.getMonth() != v) d.setDate(d.getDate() - 1);
        return d;
      },
      d: function(d:any, v:any) {
        return d.setDate(v);
      },
      p: function(d:any, v:any) {
        return d.setHours(v == 1 ? d.getHours() + 12 : d.getHours());
      },
    },
    val,
    filtered,
    s,
    cnt;
  setters_map['M'] = setters_map['MM'] = setters_map['mm'] = setters_map['m'];
  setters_map['dd'] = setters_map['d'];
  setters_map['P'] = setters_map['p'];

  var newFormat = parseFormat(format);
  var newParts: any = date.match(NON_PUNCTUATION_EXPRESSION) || [];
  date = new Date(0, 0, 0, 0, 0, 0, 0);

  // 不存在分隔符情况下
  if (newParts.length === 1) {
    var temp:any = newParts.shift();
    newFormat.parts.reduce(function (start: any, item: any) {
        var len = item.length;
        newParts.push(temp.substr(start, len));
        return start + len;
    }, 0);
  }

  for (i = 0, cnt = newFormat.parts.length; i < cnt; i++) {
    val = parseInt(newParts[i], 10);
    part = newFormat.parts[i];
    if (isNaN(val)) {
      switch (part) {
        case 'MM':
          filtered = months.filter(function(item) {
            var m = item.slice(0, newParts[i].length), // 不确定
              p = newParts[i].slice(0, m.length);
            return m == p;
          });
          val = months.indexOf(filtered[0]) + 1;
          break;
        case 'M':
          filtered = monthsShort.filter(function(item) {
            var m = item.slice(0, newParts[i].length), // 不确定
              p = newParts[i].slice(0, m.length);
            return m == p;
          });
          val = monthsShort.indexOf(filtered[0]) + 1;
          break;
        case 'p':
        case 'P':
          val = meridian.indexOf(newParts[i].toLowerCase());
          break;
      }
    }
    parsed[part] = val;
  }
  for (i = 0, s; i < setters_order.length; i++) {
    s = setters_order[i];
    if (s in parsed && !isNaN(parsed[s])) setters_map[s](date, parsed[s]);
  }
  return date;
}

function isLeapYear(year:number) {
  return (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;
}

function getDaysInMonth(year:number, month:number) {
  return [31, isLeapYear(year) ? 29 : 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31][month];
}

/**
 * 日历头部一周的names
 * @method weekDayNames
 * @param {number} firstDayOfWeek
 * @param {Array} weekDaysNames
 * @returns {Array}
 */
function weekDayNames(weekDaysNames:any, firstDayOfWeek = 0) {
  let defaultWeekDaysNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  let targetWeekDaysNames = weekDaysNames ? weekDaysNames : defaultWeekDaysNames;
  const dayShift = firstDayOfWeek % 7;
  if (dayShift) {
    targetWeekDaysNames = targetWeekDaysNames.slice(dayShift).concat(targetWeekDaysNames.slice(0, dayShift));
  }
  return targetWeekDaysNames;
}

export default {
  addYears: addYears,
  addMonths: addMonths,
  addDays: addDays,
  addHours: addHours,
  addMinutes: addMinutes,
  format: formatDate,
  parse: parseDate,
  getDaysInMonth: getDaysInMonth,
  getStartOfDay: getStartOfDay,
  getEndOfDay: getEndOfDay,
  getStartOfMonth: getStartOfMonth,
  getEndOfMonth: getEndOfMonth,
  weekDayNames: weekDayNames,
  diffYears: function(date1:Date, date2:Date) {
    return date1.getFullYear() - date2.getFullYear();
  },

  diffMonths: function(date1:Date, date2:Date) {
    var diff = this.diffYears(date1, date2) * 12;
    diff += date1.getMonth() - date2.getMonth();
    return diff;
  },

  diffDays: function(date1:Date, date2:Date) {
    var diff = date1.valueOf() - date2.valueOf();
    return diff / MILLIS_PER_DAY;
  },
};
